// Catálogo estrutural da Classificação regional (PRD §4.9) — 4 normas v1 (MG).
// Colunas, letras, notas e faixas transcritas dos PDFs oficiais versionados:
//   - decreto-regional-mg.pdf — Anexo I (indústria), Anexo II (mineração), Anexo III (água)
//   - IGAM.pdf — Portaria IGAM (Anexo IV), grades IV.1/IV.2
// Os TEXTOS dos cards/colunas vivem no i18n (4 idiomas); aqui ficam só notas + chaves.

import type {
  RegionalCatalogColumnTemplate,
  RegionalCatalogSectionTemplate,
  RegionalNorm,
  RegionalNormBands,
  RegionalNormTemplate,
  RegionalSecondSectionKey,
  RegionalState,
} from "./regional-classification.catalog.types";

const upper = (letter: string) => letter.toUpperCase();

/** Monta uma coluna da seção CRI: chave plana `cri-<grade>-<letter>`. */
const criColumn = (
  norm: RegionalNorm,
  grade: "ct" | "ec" | "psb",
  letter: string,
  scores: number[],
): RegionalCatalogColumnTemplate => {
  const colKey = `${grade}${upper(letter)}`;
  return {
    key: `cri-${grade}-${letter}`,
    letter,
    titleKey: `${norm}.columns.${colKey}`,
    options: scores.map((score) => ({
      score,
      textKey: `${norm}.options.${colKey}.score${score}`,
    })),
  };
};

/** Monta uma coluna da 2ª seção (PDA/DPA): chave plana `<pda|dpa>-<letter>`. */
const secondColumn = (
  norm: RegionalNorm,
  sectionKey: RegionalSecondSectionKey,
  letter: string,
  scores: number[],
): RegionalCatalogColumnTemplate => {
  const colKey = `${sectionKey}${upper(letter)}`;
  return {
    key: `${sectionKey}-${letter}`,
    letter,
    titleKey: `${norm}.columns.${colKey}`,
    options: scores.map((score) => ({
      score,
      textKey: `${norm}.options.${colKey}.score${score}`,
    })),
  };
};

const criSection = (
  norm: RegionalNorm,
  grades: RegionalCatalogSectionTemplate["grades"],
): RegionalCatalogSectionTemplate => ({
  key: "cri",
  labelKey: `${norm}.sections.cri.label`,
  subtitleKey: `${norm}.sections.cri.subtitle`,
  hasBignumber: true,
  grades,
});

const secondSection = (
  norm: RegionalNorm,
  sectionKey: RegionalSecondSectionKey,
  columns: RegionalCatalogColumnTemplate[],
): RegionalCatalogSectionTemplate => ({
  key: sectionKey,
  labelKey: `${norm}.sections.${sectionKey}.label`,
  subtitleKey: `${norm}.sections.${sectionKey}.subtitle`,
  hasBignumber: true,
  grades: [
    {
      key: sectionKey,
      code: sectionKey.toUpperCase(),
      fullTitleKey: `${norm}.grades.${sectionKey}`,
      columns,
    },
  ],
});

const criGrade = (
  norm: RegionalNorm,
  grade: "ct" | "ec" | "psb",
  code: string,
  columns: RegionalCatalogColumnTemplate[],
) => ({
  key: grade,
  code,
  fullTitleKey: `${norm}.grades.${grade}`,
  columns,
});

// Faixas + regra especial EC (verificadas nos PDFs):
const DECRETO_IND_MIN_BANDS: RegionalNormBands = {
  cri: { highMin: 65, mediumMin: 37, ecHighScore: 10 },
  second: { highMin: 13, mediumMin: 7 },
};
const WATER_IGAM_BANDS: RegionalNormBands = {
  cri: { highMin: 60, mediumMin: 35, ecHighScore: 8 },
  second: { highMin: 16, mediumMin: 10 },
};

// --------------------------------------------------------------------------
// Anexo I — Indústria (mgIndustry): CT a–e, EC f–i, PSB j–n, PDA a–d
// --------------------------------------------------------------------------
const mgIndustry: RegionalNormTemplate = {
  norm: "mgIndustry",
  state: "mg",
  labelKey: "norms.mgIndustry",
  secondSectionKey: "pda",
  bands: DECRETO_IND_MIN_BANDS,
  sections: [
    criSection("mgIndustry", [
      criGrade("mgIndustry", "ct", "CT", [
        criColumn("mgIndustry", "ct", "a", [0, 1, 4, 7]),
        criColumn("mgIndustry", "ct", "b", [0, 1, 2, 3]),
        criColumn("mgIndustry", "ct", "c", [0, 2, 5, 10]),
        criColumn("mgIndustry", "ct", "d", [1, 3]),
        criColumn("mgIndustry", "ct", "e", [0, 2, 6, 8]),
      ]),
      criGrade("mgIndustry", "ec", "EC", [
        criColumn("mgIndustry", "ec", "f", [0, 3, 6, 10]),
        criColumn("mgIndustry", "ec", "g", [0, 3, 6, 10]),
        criColumn("mgIndustry", "ec", "h", [0, 2, 6, 10]),
        criColumn("mgIndustry", "ec", "i", [0, 2, 6, 10]),
      ]),
      criGrade("mgIndustry", "psb", "PSB", [
        criColumn("mgIndustry", "psb", "j", [0, 2, 3, 5, 8, 10]),
        criColumn("mgIndustry", "psb", "k", [0, 1, 3, 6]),
        criColumn("mgIndustry", "psb", "l", [0, 2, 4, 8]),
        criColumn("mgIndustry", "psb", "m", [0, 2, 4, 8]),
        criColumn("mgIndustry", "psb", "n", [0, 2, 4, 6, 8]),
      ]),
    ]),
    secondSection("mgIndustry", "pda", [
      secondColumn("mgIndustry", "pda", "a", [1, 2, 3, 4, 5]),
      secondColumn("mgIndustry", "pda", "b", [0, 3, 5, 10]),
      secondColumn("mgIndustry", "pda", "c", [0, 2, 4, 10]),
      secondColumn("mgIndustry", "pda", "d", [0, 1, 3, 5]),
    ]),
  ],
};

// --------------------------------------------------------------------------
// Anexo II — Mineração (mgMining): CT a–e, EC f–i, PSB j–n, PDA a–d
// Difere de Indústria em CT(d) Método e PDA(c) Impacto ambiental.
// --------------------------------------------------------------------------
const mgMining: RegionalNormTemplate = {
  norm: "mgMining",
  state: "mg",
  labelKey: "norms.mgMining",
  secondSectionKey: "pda",
  bands: DECRETO_IND_MIN_BANDS,
  sections: [
    criSection("mgMining", [
      criGrade("mgMining", "ct", "CT", [
        criColumn("mgMining", "ct", "a", [0, 1, 4, 7]),
        criColumn("mgMining", "ct", "b", [0, 1, 2, 3]),
        criColumn("mgMining", "ct", "c", [0, 2, 5, 10]),
        criColumn("mgMining", "ct", "d", [0, 2, 5, 10]),
        criColumn("mgMining", "ct", "e", [0, 2, 6, 8]),
      ]),
      criGrade("mgMining", "ec", "EC", [
        criColumn("mgMining", "ec", "f", [0, 3, 6, 10]),
        criColumn("mgMining", "ec", "g", [0, 3, 6, 10]),
        criColumn("mgMining", "ec", "h", [0, 2, 6, 10]),
        criColumn("mgMining", "ec", "i", [0, 2, 6, 10]),
      ]),
      criGrade("mgMining", "psb", "PSB", [
        criColumn("mgMining", "psb", "j", [0, 2, 3, 5, 8, 10]),
        criColumn("mgMining", "psb", "k", [0, 1, 3, 6]),
        criColumn("mgMining", "psb", "l", [0, 2, 4, 8]),
        criColumn("mgMining", "psb", "m", [0, 2, 4, 8]),
        criColumn("mgMining", "psb", "n", [0, 2, 4, 6, 8]),
      ]),
    ]),
    secondSection("mgMining", "pda", [
      secondColumn("mgMining", "pda", "a", [1, 2, 3, 4, 5]),
      secondColumn("mgMining", "pda", "b", [0, 3, 5, 10]),
      secondColumn("mgMining", "pda", "c", [0, 2, 6, 8, 10]),
      secondColumn("mgMining", "pda", "d", [0, 1, 3, 5]),
    ]),
  ],
};

// --------------------------------------------------------------------------
// Anexo III — Água (mgWater): CT a–f, EC g–k, PSB l–p, PDA a–d
// --------------------------------------------------------------------------
const mgWater: RegionalNormTemplate = {
  norm: "mgWater",
  state: "mg",
  labelKey: "norms.mgWater",
  secondSectionKey: "pda",
  bands: WATER_IGAM_BANDS,
  sections: [
    criSection("mgWater", [
      criGrade("mgWater", "ct", "CT", [
        criColumn("mgWater", "ct", "a", [0, 1, 2, 3]),
        criColumn("mgWater", "ct", "b", [2, 3]),
        criColumn("mgWater", "ct", "c", [1, 2, 3]),
        criColumn("mgWater", "ct", "d", [1, 2, 3, 4, 5]),
        criColumn("mgWater", "ct", "e", [1, 2, 3, 4]),
        criColumn("mgWater", "ct", "f", [3, 5, 8, 10]),
      ]),
      criGrade("mgWater", "ec", "EC", [
        criColumn("mgWater", "ec", "g", [0, 4, 7, 10]),
        criColumn("mgWater", "ec", "h", [0, 4, 6]),
        criColumn("mgWater", "ec", "i", [0, 3, 5, 8]),
        criColumn("mgWater", "ec", "j", [0, 1, 5, 8]),
        criColumn("mgWater", "ec", "k", [0, 1, 5, 7]),
      ]),
      criGrade("mgWater", "psb", "PSB", [
        criColumn("mgWater", "psb", "l", [0, 2, 4, 6, 8]),
        criColumn("mgWater", "psb", "m", [0, 4, 8]),
        criColumn("mgWater", "psb", "n", [0, 3, 5, 6]),
        criColumn("mgWater", "psb", "o", [0, 6]),
        criColumn("mgWater", "psb", "p", [0, 3, 5]),
      ]),
    ]),
    secondSection("mgWater", "pda", [
      secondColumn("mgWater", "pda", "a", [0, 1, 2, 3, 4]),
      secondColumn("mgWater", "pda", "b", [0, 4, 8, 12]),
      secondColumn("mgWater", "pda", "c", [1, 2, 5]),
      secondColumn("mgWater", "pda", "d", [0, 1, 3, 8]),
    ]),
  ],
};

// --------------------------------------------------------------------------
// Portaria IGAM (igam): CT a–f, EC g–l, PSB m–q, DPA a–d
// --------------------------------------------------------------------------
const igam: RegionalNormTemplate = {
  norm: "igam",
  state: "mg",
  labelKey: "norms.igam",
  secondSectionKey: "dpa",
  bands: WATER_IGAM_BANDS,
  sections: [
    criSection("igam", [
      criGrade("igam", "ct", "CT", [
        criColumn("igam", "ct", "a", [0, 1, 2, 3]),
        criColumn("igam", "ct", "b", [2, 3]),
        criColumn("igam", "ct", "c", [1, 2, 3]),
        criColumn("igam", "ct", "d", [1, 2, 3, 4, 5]),
        criColumn("igam", "ct", "e", [1, 2, 3, 4]),
        criColumn("igam", "ct", "f", [3, 5, 8, 10]),
      ]),
      criGrade("igam", "ec", "EC", [
        criColumn("igam", "ec", "g", [0, 4, 7, 10]),
        criColumn("igam", "ec", "h", [0, 4, 6]),
        criColumn("igam", "ec", "i", [0, 3, 5, 8]),
        criColumn("igam", "ec", "j", [0, 1, 5, 8]),
        criColumn("igam", "ec", "k", [0, 1, 5, 7]),
        criColumn("igam", "ec", "l", [0, 1, 2, 4]),
      ]),
      criGrade("igam", "psb", "PS", [
        criColumn("igam", "psb", "m", [0, 2, 4, 6, 8]),
        criColumn("igam", "psb", "n", [0, 4, 8]),
        criColumn("igam", "psb", "o", [0, 3, 5, 6]),
        criColumn("igam", "psb", "p", [0, 6]),
        criColumn("igam", "psb", "q", [0, 3, 5]),
      ]),
    ]),
    secondSection("igam", "dpa", [
      secondColumn("igam", "dpa", "a", [1, 2, 3, 5]),
      secondColumn("igam", "dpa", "b", [0, 4, 8, 12]),
      secondColumn("igam", "dpa", "c", [1, 2, 5]),
      secondColumn("igam", "dpa", "d", [0, 1, 3, 8]),
    ]),
  ],
};

export const REGIONAL_CATALOG_TEMPLATE: Record<
  RegionalNorm,
  RegionalNormTemplate
> = {
  mgIndustry,
  mgMining,
  mgWater,
  igam,
};

/**
 * Normas ofertadas por estado — derivado do campo `state` de cada norma (fonte
 * única de verdade). É a fonte do radio de normas APÓS a escolha da UF: o select
 * de Estado lista `REGIONAL_STATES`; escolhido o estado, o radio mostra
 * `REGIONAL_NORMS_BY_STATE[state]`. Preserva a ordem de declaração das normas.
 * v1: `{ mg: [mgIndustry, mgMining, mgWater, igam] }`.
 */
export const REGIONAL_NORMS_BY_STATE: Record<RegionalState, RegionalNorm[]> = (
  Object.keys(REGIONAL_CATALOG_TEMPLATE) as RegionalNorm[]
).reduce(
  (acc, norm) => {
    const { state } = REGIONAL_CATALOG_TEMPLATE[norm];
    (acc[state] ??= []).push(norm);
    return acc;
  },
  {} as Record<RegionalState, RegionalNorm[]>,
);
